import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import type { User } from "@/lib/types";
import { Link } from "@/lib/router";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Trophy, Medal, Award, TrendingUp, DollarSign, Code } from "lucide-react";

interface RankEntry {
	user: User;
	rank: number;
	score: number;
}

const rankIcons = [
	{ icon: Trophy, color: "text-amber-500", bg: "bg-amber-500/10" },
	{ icon: Medal, color: "text-zinc-400", bg: "bg-zinc-400/10" },
	{ icon: Award, color: "text-amber-700", bg: "bg-amber-700/10" },
];

export function Leaderboard() {
	const [rankings, setRankings] = useState<RankEntry[]>([]);

	useEffect(() => {
		api.getRankings().then(setRankings);
	}, []);

	return (
		<div className="container mx-auto px-4 py-8 max-w-3xl">
			<div className="mb-8">
				<h1 className="text-3xl font-bold tracking-tight mb-2">Creator Leaderboard</h1>
				<p className="text-muted-foreground">
					Top builders ranked by wins, earnings, and activity.
				</p>
			</div>

			{/* Top 3 podium */}
			{rankings.length >= 3 && (
				<div className="grid grid-cols-3 gap-4 mb-8">
					{[1, 0, 2].map((idx) => {
						const entry = rankings[idx];
						const config = rankIcons[idx];
						const Icon = config.icon;
						const isFirst = idx === 0;
						return (
							<Link
								key={entry.user.id}
								to={`/profile/${entry.user.id}`}
								className="block group"
							>
								<Card
									className={`text-center transition-all group-hover:shadow-md ${
										isFirst ? "border-amber-500/30 bg-amber-500/5 -mt-4" : ""
									}`}
								>
									<CardContent className="pt-6 pb-5">
										<div
											className={`inline-flex h-12 w-12 items-center justify-center rounded-full ${config.bg} mb-3`}
										>
											<Icon className={`h-6 w-6 ${config.color}`} />
										</div>
										<div className="flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground text-lg font-bold mx-auto mb-2">
											{entry.user.avatar}
										</div>
										<p className="font-semibold text-sm mb-1">
											{entry.user.username}
										</p>
										<p className="text-xs text-muted-foreground mb-2">
											Score: {Math.round(entry.score)}
										</p>
										<div className="flex items-center justify-center gap-3 text-xs">
											<span className="text-emerald-600 font-medium">
												{entry.user.wins} wins
											</span>
											<span className="text-muted-foreground">
												${entry.user.totalEarnings}
											</span>
										</div>
									</CardContent>
								</Card>
							</Link>
						);
					})}
				</div>
			)}

			{/* Full ranking table */}
			<Card>
				<CardContent className="pt-0 pb-0 divide-y">
					{rankings.map((entry) => (
						<Link
							key={entry.user.id}
							to={`/profile/${entry.user.id}`}
							className="flex items-center gap-4 py-4 hover:bg-secondary/30 -mx-6 px-6 transition-colors"
						>
							<div className="w-8 text-center">
								{entry.rank <= 3 ? (
									(() => {
										const config = rankIcons[entry.rank - 1];
										const Icon = config.icon;
										return <Icon className={`h-5 w-5 mx-auto ${config.color}`} />;
									})()
								) : (
									<span className="text-sm font-bold text-muted-foreground">
										#{entry.rank}
									</span>
								)}
							</div>

							<div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold shrink-0">
								{entry.user.avatar}
							</div>

							<div className="flex-1 min-w-0">
								<p className="font-semibold text-sm">{entry.user.username}</p>
								<p className="text-xs text-muted-foreground line-clamp-1">
									{entry.user.bio}
								</p>
							</div>

							<div className="hidden sm:flex items-center gap-6 text-xs text-muted-foreground shrink-0">
								<span className="flex items-center gap-1" title="Wins">
									<Trophy className="h-3.5 w-3.5 text-amber-500" />
									<span className="font-medium text-foreground">{entry.user.wins}</span>
								</span>
								<span className="flex items-center gap-1" title="Earnings">
									<DollarSign className="h-3.5 w-3.5 text-emerald-500" />
									<span className="font-medium text-foreground">
										${entry.user.totalEarnings}
									</span>
								</span>
								<span className="flex items-center gap-1" title="Submissions">
									<Code className="h-3.5 w-3.5 text-blue-500" />
									<span className="font-medium text-foreground">
										{entry.user.submissionsCreated}
									</span>
								</span>
							</div>

							<div className="text-right shrink-0">
								<div className="flex items-center gap-1 text-sm font-bold">
									<TrendingUp className="h-3.5 w-3.5 text-primary" />
									{Math.round(entry.score)}
								</div>
								<p className="text-xs text-muted-foreground">score</p>
							</div>
						</Link>
					))}
				</CardContent>
			</Card>
		</div>
	);
}
